import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Cart.css';
import AedPrice from '../components/AedPrice';

const Cart = () => {
  const { items, removeItem, updateQuantity, getSubtotal, getShipping, getTotal, getDisplayPrice, isEmpty } = useCart();
  const { isAuthenticated } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const navigate = useNavigate();

  const formatPrice = (price) => <AedPrice amount={price} />;

  const getItemName = (item) => {
    return language === 'ar' && item.nameAr ? item.nameAr : item.name;
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
    } else {
      navigate('/checkout');
    }
  };

  if (isEmpty) {
    return (
      <div className="container">
        <div className="empty-cart">
          <span className="empty-icon"><svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg></span>
          <h2>{t('emptyCart')}</h2>
          <p>{t('emptyCartDesc')}</p>
          <Link to="/products" className="btn btn-primary btn-lg">
            {t('startShopping')}
          </Link>
        </div>
      </div>
    );
  }

  const subtotal = getSubtotal();
  const shipping = getShipping();
  const total = subtotal + shipping;

  return (
    <div className={`cart-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <div className="page-header">
          <h1>{t('shoppingCart')}</h1>
        </div>

        <div className="cart-layout">
          <div className="cart-items">
            {items.map(item => (
              <div key={item.id} className="cart-item">
                <div className="item-image">
                  {item.hideImage ? (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      background: '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                    </div>
                  ) : (
                    <img src={item.image} alt={item.name} />
                  )}
                </div>

                <div className="item-details">
                  <Link to={`/products/${item.id}`} className="item-name">
                    {getItemName(item)}
                  </Link>
                  <p className="item-price">
                    {item.onSale && item.salePrice ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '13px' }}>{formatPrice(item.price)}</span>
                        {' '}
                        <span style={{ color: '#dc2626', fontWeight: '600' }}>{formatPrice(item.salePrice)}</span>
                      </>
                    ) : (
                      formatPrice(item.price)
                    )}
                    {' × '}{item.quantity}
                  </p>
                </div>

                <div className="item-quantity">
                  <button
                    className="qty-btn"
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                    aria-label={isRTL ? 'تقليل الكمية' : 'Decrease quantity'}
                  >
                    −
                  </button>
                  <span className="qty-value">{item.quantity}</span>
                  <button
                    className="qty-btn"
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    aria-label={isRTL ? 'زيادة الكمية' : 'Increase quantity'}
                  >
                    +
                  </button>
                </div>

                <div className="item-total">
                  {formatPrice(getDisplayPrice(item) * item.quantity)}
                </div>

                <button
                  className="remove-btn"
                  onClick={() => removeItem(item.id)}
                  aria-label={isRTL ? 'إزالة المنتج' : 'Remove item'}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h3>{t('orderSummary')}</h3>

            <div className="summary-details">
              <div className="summary-row">
                <span>{t('subtotal')}</span>
                <span className="summary-value">{formatPrice(subtotal)}</span>
              </div>

              <div className="summary-row">
                <span>{t('shipping')}</span>
                <span className="summary-value">{formatPrice(shipping)}</span>
              </div>
            </div>

            <div className="summary-total">
              <span>{t('total')}</span>
              <span className="total-value">{formatPrice(total)}</span>
            </div>

            <button
              className="btn btn-primary btn-lg checkout-btn"
              onClick={handleCheckout}
            >
              {t('proceedToCheckout')}
            </button>

            <Link to="/products" className="continue-shopping">
              {isRTL ? `${t('continueShopping')} ←` : `→ ${t('continueShopping')}`}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
